"use client"

import { useState, useEffect } from "react"
import { Play, Check } from "lucide-react"
import { GlassSidePanel } from "@/src/components/ui/glass-side-panel"
import { Z } from "@/lib/z-index"
import type { DemoScenario } from "@/lib/demo-scenarios"

/**
 * DemoScenariosPanel — Uses GlassSidePanel for the shell, provides scenario content.
 * Starts collapsed, auto-expands after 500ms.
 */
export function DemoScenariosPanel({
  scenarioList,
  selectedScenarioId,
  onSelectScenario,
  visible = true,
  defaultCollapsed = false,
}: {
  scenarioList: Omit<DemoScenario, "steps">[]
  selectedScenarioId?: string
  onSelectScenario: (id: string) => void
  visible?: boolean
  /** If true, starts collapsed with no auto-expand (e.g. in chat-session) */
  defaultCollapsed?: boolean
}) {
  // Start collapsed; auto-expand after 500ms only if not defaultCollapsed
  const [collapsed, setCollapsed] = useState(true)
  useEffect(() => {
    if (defaultCollapsed) {
      setCollapsed(true)
      return
    }
    const t = setTimeout(() => setCollapsed(false), 500)
    return () => clearTimeout(t)
  }, [defaultCollapsed])

  const isOpen = visible && !collapsed

  return (
    <GlassSidePanel
      side="left"
      open={isOpen}
      onOpenChange={(open) => setCollapsed(!open)}
      capsuleLabel="使用案例"
      width={16}
      layout="center"
      panelStyleClass="demo-scenarios-panel"
      zIndex={Z.NAV_PILL - 2}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-border/30 px-4 flex-shrink-0" style={{ minHeight: 50, paddingTop: 8, paddingBottom: 8 }}>
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 flex-shrink-0">
          <Play className="h-3.5 w-3.5 text-primary fill-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">大家都在用</h3>
          <p className="text-micro text-muted-foreground">热门使用案例</p>
        </div>
      </div>

      {/* Scenario list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 glass-scroll" style={{ overscrollBehavior: "contain", contain: "content" }}>
        <div className="flex flex-col gap-1">
          {scenarioList.map((s) => {
            const isSelected = selectedScenarioId === s.id
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelectScenario(s.id)}
                className={`flex items-center gap-2.5 rounded-[14px] px-3 py-2.5 text-left transition-all duration-200 ease-out group w-full ${
                  isSelected
                    ? "bg-primary/5 border border-border/40 shadow-[0_1px_0_0_rgba(255,255,255,0.2)_inset,0_2px_8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.05)] -translate-y-px"
                    : "border border-transparent shadow-none translate-y-0 hover:bg-secondary/50 hover:border-border/30"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${
                    isSelected ? "text-primary" : "text-foreground/80"
                  }`}>
                    {s.title}
                  </p>
                  <p className="text-micro text-muted-foreground truncate mt-0.5">
                    {s.description}
                  </p>
                </div>
                {isSelected && (
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 animate-in zoom-in duration-200" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border/30 px-4 py-2 flex-shrink-0">
        <p className="text-micro text-muted-foreground/50">
          {scenarioList.length} 个使用案例
        </p>
      </div>
    </GlassSidePanel>
  )
}
